<template>
  <div
    :class="['face-item', { 'face-item--selected': selected }]"
    @click="$emit('select')"
  >
    <q-avatar
      size="80px"
      color="grey-3"
      class="face-item__avatar"
    >
      <q-img
        v-if="face.imageUrl"
        :src="face.imageUrl"
        :ratio="1"
        fit="cover"
      />
      <q-icon
        v-else
        name="fa fa-user"
        size="36px"
        color="grey-5"
      />
    </q-avatar>

    <div v-if="isGranted('MANAGE_FACES')" class="face-item__actions">
      <q-btn
        icon="fa fa-user-plus"
        color="white"
        flat
        round
        dense
        class="face-action-btn"
        @click.stop="openAssignDialog"
      >
        <q-tooltip :delay="400">Reassign to person</q-tooltip>
      </q-btn>
      <q-btn
        icon="fa fa-trash"
        color="white"
        :loading="isLoading"
        flat
        round
        dense
        class="face-action-btn"
        @click.stop="confirmDelete"
      >
        <q-tooltip :delay="400">Delete face</q-tooltip>
      </q-btn>
    </div>

    <AssignFaceModal
      ref="assignModal"
      :client-id="clientId"
      @assigned="$emit('reassigned', face)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Face } from '@/analysis/types';
import { deleteFace } from '@/analysis/face/face';
import isGranted from '@/auth/isGranted';
import notify from '@/components/notify';
import * as dialog from '@/components/modal/dialog';
import AssignFaceModal from '@/analysis/face/AssignFaceModal.vue';
import { HasTaskMixin } from '@/mixins';


export default defineComponent({
  emits: ['deleted', 'reassigned', 'select'],
  mixins: [HasTaskMixin],
  components: { AssignFaceModal },
  props: {
    face: { type: Object as () => Face, required: true },
    clientId: { type: Number, required: true },
    selected: { type: Boolean, default: false },
  },
  methods: {
    isGranted,
    openAssignDialog() {
      (this.$refs.assignModal as any).open(this.face);
    },
    confirmDelete() {
      dialog.confirm({
        parent: this,
        title: 'Delete Face',
        message: 'Permanently delete this face? This will remove it from all locations, delete related post tags, and cannot be undone.',
      }).onOk(() => this.doDelete());
    },
    async doDelete() {
      this.task = deleteFace(this.face.id);
      this.task.then(() => {
        notify('Face deleted successfully.', 'positive');
        this.$emit('deleted', this.face);
      });
      this.task.catch(() => {
        notify('Failed to delete face.', 'negative');
      });
    },
  },
});
</script>

<style scoped>
.face-item {
  position: relative;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  line-height: 0;
  display: inline-block;
  padding: 5px;
}

.face-item--selected {
  box-shadow: 0 0 0 2px black;
}

.face-item__actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 0;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 0 0 40px 40px;
  padding: 4px 0 6px;
  opacity: 0;
  transition: opacity 0.15s;
  overflow: hidden;
}

.face-item:hover .face-item__actions {
  opacity: 1;
}

.face-item__avatar {
  display: block;
}

.face-action-btn {
  font-size: 10px;
  min-height: 24px;
  min-width: 24px;
  padding: 0;
}
</style>
